import React from 'react';
import { Popover } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import styles from './index.module.css';
import { UberLiveMapInline } from '../UberDriverMap/UberLiveMapInline';
import { RIDE_STATUSES } from '../../../constants/constants';
import { extractUberPhoneDigits, formatUberPhoneForDisplay } from '../../../utils/helpers';

const getUberMapFlags = (record) => {
  const uberStatus = record?.uberInfo?.uberStatus;
  const isUberCompleted = uberStatus === 'completed' || record?.status === RIDE_STATUSES.COMPLETED;
  const isUberInProgress = record?.status === RIDE_STATUSES.IN_PROGRESS && !isUberCompleted;
  const canShowMap = isUberInProgress || isUberCompleted;
  const pickupAddress = record?.pickupAddressDetails?.formattedAddress ?? record?.pickupFormattedAddress;
  const destinationAddress = record?.destinationAddress?.formattedAddress ?? record?.destinationFormattedAddress;
  return { uberStatus, isUberCompleted, isUberInProgress, canShowMap, pickupAddress, destinationAddress };
};

const PopoverContent = ({ record }) => {
  const driverInfo = record?.uberInfo?.driverInfo;
  const driverName = driverInfo?.name;
  const uberPhoneDigits = extractUberPhoneDigits({
    phoneValue: record?.uberInfo?.driver?.phone || driverInfo?.phone || record?.driverPhoneNumber,
    emailValue: record?.uberInfo?.driver?.email,
  });
  const driverPhone = formatUberPhoneForDisplay(uberPhoneDigits) || null;
  const vehicleMake = driverInfo?.vehicleMake;
  const vehicleModel = driverInfo?.vehicleModel;
  const licensePlate = driverInfo?.licensePlate;

  const { uberStatus, isUberCompleted, canShowMap, pickupAddress, destinationAddress } = getUberMapFlags(record);

  const hasDriverIdentity = driverName || driverPhone;
  const hasVehicleInfo = vehicleMake || vehicleModel || licensePlate;
  const hasInfo = hasDriverIdentity || hasVehicleInfo;

  return (
    <div className={styles.content} onClick={(e) => e.stopPropagation()}>
      {canShowMap && (
        <div className={styles.mapContainer}>
          <UberLiveMapInline
            rideId={record?.id}
            uberStatus={uberStatus}
            pickupAddress={pickupAddress}
            destinationAddress={destinationAddress}
            lastLocation={record?.uberInfo?.lastLocation}
            apiUrl={process.env.REACT_APP_API_URL}
            isCompleted={isUberCompleted}
            height="320px"
          />
        </div>
      )}

      {hasInfo && (
        <div className={styles.infoRow}>
          {hasDriverIdentity && (
            <>
              <div className={styles.infoCell}>
                <span className={styles.label}>Driver Name</span>
                <span className={styles.value}>{driverName || '—'}</span>
              </div>
              <div className={styles.infoCell}>
                <span className={styles.label}>Phone number</span>
                <span className={styles.value}>{driverPhone || '—'}</span>
              </div>
            </>
          )}
          {hasVehicleInfo && (
            <>
              <div className={styles.infoCell}>
                <span className={styles.label}>Make</span>
                <span className={styles.value}>{vehicleMake || '—'}</span>
              </div>
              <div className={styles.infoCell}>
                <span className={styles.label}>Model</span>
                <span className={styles.value}>{vehicleModel || '—'}</span>
              </div>
              <div className={styles.infoCell}>
                <span className={styles.label}>Number Plate</span>
                <span className={styles.value}>{licensePlate || '—'}</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const RideDriverPopover = ({ record }) => {
  const isUberRide = record?.vehicleType === 'uberAmbulatoryRide';
  const { canShowMap } = getUberMapFlags(record);
  const driverInfo = record?.uberInfo?.driverInfo;
  const hasDriverName = !!driverInfo?.name;
  const hasVehicleDetail = !!(driverInfo?.vehicleMake || driverInfo?.vehicleModel || driverInfo?.licensePlate);
  const hasPhone = !!extractUberPhoneDigits({
    phoneValue: record?.uberInfo?.driver?.phone || driverInfo?.phone || record?.driverPhoneNumber,
    emailValue: record?.uberInfo?.driver?.email,
  });
  const hasAnyContent = canShowMap || hasDriverName || hasVehicleDetail || hasPhone;

  if (!isUberRide || !hasAnyContent) return null;

  return (
    <Popover
      content={<PopoverContent record={record} />}
      trigger="hover"
      placement="right"
      overlayClassName={styles.overlay}
      destroyTooltipOnHide={false}
    >
      <EnvironmentOutlined
        className={styles.icon}
        onClick={(e) => e.stopPropagation()}
      />
    </Popover>
  );
};
